import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { T } from "@/lib/theme";
import { WaveMark } from "@/components/WaveMark";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export function NavBar({ dark = false }) {
  const { user, logout, isLoggedIn } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);

  const bg      = dark ? T.dark.bg      : "#fff";
  const color   = dark ? T.dark.text    : T.navy;
  const border  = dark ? T.dark.border  : T.border;
  const muted   = dark ? T.dark.muted   : T.muted;
  const surface = dark ? T.dark.surface : "#fff";

  const linkStyle = (active) => ({
    background: "none", border: "none",
    color: active ? T.greenAccent : color,
    cursor: "pointer", fontSize: 13,
    fontWeight: active ? 700 : 500,
    padding: "6px 4px",
  });

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  // Auto-close mobile drawer on route change so users land on the new page cleanly
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Inside the persistent AppShell (/app/*) the shell's topbar replaces the
  // marketing NavBar — avoid double-headers. (Early return AFTER all hooks.)
  if (loc.pathname.startsWith("/app/")) return null;

  const initials = (user?.full_name || user?.email || "?")
    .split(/\s+|@/).filter(Boolean)
    .map((s) => s[0]?.toUpperCase()).slice(0, 2).join("");

  const go = (path) => {
    if (path.includes("#")) {
      const [base, id] = path.split("#");
      nav(base || "/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
    } else nav(path);
  };

  // Listen is open to everyone — registered or not. It's the engagement front door.
  // Fortune-500 clean directive: only Listen + auth CTAs on the public nav.
  // All secondary marketing content lives on /product (linked from footer).
  const publicLinks = [
    { label: "Listen", path: "/listen", testid: "nav-listen-public" },
  ];

  const navLinks = isLoggedIn ? [
    // Dashboard + Claim are visible to ALL authenticated users now; advertisers
    // who claim a station are auto-promoted to station_manager server-side.
    { label: "Dashboard", path: "/dashboard", testid: "nav-dashboard" },
    ...(user?.role === "advertiser" || user?.role === "brand" || user?.role === "admin"
      ? [{ label: "AdVerify", path: "/campaigns", testid: "nav-campaigns" }] : []),
    { label: "Plan",   path: "/plan",   testid: "nav-plan"   },
    { label: "Map",    path: "/map",    testid: "nav-map"    },
    { label: "Listen", path: "/listen", testid: "nav-listen" },
    { label: "Claim Station", path: "/claim", testid: "nav-claim" },
  ] : [];

  return (
    <>
      <nav style={{ background: bg, borderBottom: `1px solid ${border}`, padding: "14px 32px",
        display: "flex", justifyContent: "space-between", alignItems: "center" }} data-testid="navbar">
        <div onClick={() => nav("/")} data-testid="navbar-logo"
          style={{ cursor: "pointer", fontWeight: 800, fontSize: 18, color, display: "flex", alignItems: "center", gap: 10 }}>
          <WaveMark size={28} inverted={dark} />
          <span>WaveScore<span style={{ color: T.greenAccent }}>Pro</span></span>
        </div>

        {/* Desktop nav links — hidden below 768px via App.css .navbar-desktop */}
        <div data-testid="navbar-desktop-links" className="navbar-desktop"
          style={{ display: "flex", gap: 18, alignItems: "center" }}>
          {isLoggedIn ? (
            <>
              {navLinks.map((l) => (
                <button key={l.path} data-testid={l.testid}
                  style={linkStyle(loc.pathname.startsWith(l.path))} onClick={() => go(l.path)}>
                  {l.label}
                </button>
              ))}
              <div ref={menuRef} style={{ position: "relative" }}>
                <button data-testid="nav-avatar" onClick={() => setMenuOpen((v) => !v)}
                  style={{ background: T.greenAccent + "22", border: `1px solid ${T.greenAccent}55`,
                    color: T.greenAccent, width: 34, height: 34, borderRadius: "50%",
                    cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 800, letterSpacing: "0.04em" }}>
                  {initials || "?"}
                </button>
                {menuOpen && (
                  <div data-testid="nav-avatar-menu" style={{ position: "absolute", right: 0, top: 44,
                    background: surface, border: `1px solid ${border}`, borderRadius: 10, padding: 8,
                    minWidth: 220, zIndex: 50, boxShadow: "0 10px 30px rgba(0,0,0,0.18)" }}>
                    <div style={{ padding: "8px 10px", borderBottom: `1px solid ${border}`, marginBottom: 4 }}>
                      <div style={{ color, fontSize: 13, fontWeight: 700 }}>{user?.full_name || "Account"}</div>
                      <div style={{ color: muted, fontSize: 11, marginTop: 2, wordBreak: "break-all" }}>{user?.email}</div>
                      <div style={{ marginTop: 6, display: "inline-block", color: T.greenAccent, fontSize: 10,
                        fontWeight: 700, letterSpacing: "0.08em", background: T.greenAccent + "22",
                        padding: "2px 8px", borderRadius: 4 }}>{user?.role?.toUpperCase()}</div>
                    </div>
                    <MenuItem testid="menu-profile" label="Profile & settings" onClick={() => { setMenuOpen(false); nav("/profile"); }} color={color} />
                    <MenuItem testid="menu-billing" label="Billing & plan" onClick={() => { setMenuOpen(false); nav("/billing"); }} color={color} />
                    {user?.role === "admin" && (
                      <MenuItem testid="menu-admin" label="Admin console" onClick={() => { setMenuOpen(false); nav("/admin"); }} color={color} />
                    )}
                    <div style={{ height: 1, background: border, margin: "6px 4px" }} />
                    <MenuItem testid="menu-signout" label="Sign out" onClick={() => { setMenuOpen(false); logout(); nav("/"); }} color="#EF4444" />
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {publicLinks.map((l) => (
                <button key={l.path} data-testid={l.testid}
                  style={linkStyle(loc.pathname.startsWith(l.path))} onClick={() => go(l.path)}>
                  {l.label}
                </button>
              ))}
              <button data-testid="nav-signin" style={linkStyle(false)} onClick={() => nav("/login")}>Sign in</button>
              <button data-testid="nav-get-started" onClick={() => nav("/register")}
                style={{ background: T.greenAccent, border: "none", color: "#fff",
                  cursor: "pointer", fontSize: 13, fontWeight: 700, padding: "8px 18px", borderRadius: 8 }}>
                Get started free
              </button>
            </>
          )}
          {/* LanguageSwitcher sits outside the auth ternary so it renders for
              signed-in users too. Sibling of the auth block, not a child. */}
          <div style={{ color, opacity: 0.8, marginLeft: 4 }}>
            <LanguageSwitcher />
          </div>
        </div>

        {/* Hamburger — visible only below 768px */}
        <button data-testid="navbar-hamburger" className="navbar-hamburger"
          onClick={() => setMobileOpen((v) => !v)} aria-label="Open navigation menu"
          style={{ display: "none", background: "none", border: "none", cursor: "pointer",
            padding: 4, color, fontSize: 22, lineHeight: 1 }}>
          {mobileOpen ? "✕" : "☰"}
        </button>
      </nav>

      {/* Mobile drawer — only renders when open; CSS toggles its visibility */}
      {mobileOpen && (
        <div data-testid="navbar-mobile-drawer" className="navbar-mobile-drawer"
          style={{ background: surface, borderBottom: `1px solid ${border}`,
            padding: "12px 24px 20px", display: "none" }}>
          {isLoggedIn ? (
            <>
              {navLinks.map((l) => (
                <button key={l.path} data-testid={`mobile-${l.testid}`} onClick={() => go(l.path)}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 0",
                    background: "none", border: "none", borderBottom: `1px solid ${border}`,
                    color: loc.pathname.startsWith(l.path) ? T.greenAccent : color,
                    fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  {l.label}
                </button>
              ))}
              <button onClick={() => nav("/profile")}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 0",
                  background: "none", border: "none", borderBottom: `1px solid ${border}`,
                  color, fontSize: 15, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
                Profile & settings
              </button>
              <button onClick={() => nav("/billing")}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 0",
                  background: "none", border: "none", borderBottom: `1px solid ${border}`,
                  color, fontSize: 15, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
                Billing & plan
              </button>
              <button onClick={() => { logout(); nav("/"); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "14px 0",
                  background: "none", border: "none", color: "#EF4444",
                  fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Sign out
              </button>
            </>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 8 }}>
              <button data-testid="mobile-nav-listen-public" onClick={() => nav("/listen")}
                style={{ padding: "12px", border: `1px solid ${T.greenAccent}55`, borderRadius: 8,
                  background: T.greenAccent + "11", color: T.greenAccent, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                🎧 Listen Live — Free
              </button>
              <button onClick={() => nav("/login")}
                style={{ padding: "12px", border: `1px solid ${border}`, borderRadius: 8,
                  background: "none", color, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                Sign in
              </button>
              <button onClick={() => nav("/register")}
                style={{ padding: "12px", background: T.greenAccent, border: "none", borderRadius: 8,
                  color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                Get started free
              </button>
            </div>
          )}
          {/* Language switcher on mobile — surfaces the same choice as desktop. */}
          <div style={{ marginTop: 12, padding: "8px 4px", borderTop: `1px solid ${border}`, color }}>
            <LanguageSwitcher variant="full" />
          </div>
        </div>
      )}
    </>
  );
}

function MenuItem({ label, onClick, color, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px",
        background: "transparent", border: "none", color, cursor: "pointer",
        fontFamily: "inherit", fontSize: 13, fontWeight: 500, borderRadius: 6 }}
      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,200,150,0.08)"}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
      {label}
    </button>
  );
}
